import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Plus, Pencil, Trash2, Search, Check, X, GitMerge,
} from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/ConfirmDialog';

/**
 * Generic library manager — drives both Document Types and Library Tags.
 *
 * Props:
 *   title, description, testIdPrefix
 *   categories: string[]
 *   api: { list, create, update, delete, approve, reject }
 *   labels: { singular, plural, name, search }
 *   nameTransform?: (s) => s   (e.g. lowercase tags)
 *   extraFields?: array of { key, label, type:'text'|'textarea', placeholder? }
 *   showSynonyms?: boolean
 */
export default function LibraryManager({
  title, description, testIdPrefix,
  categories, api, labels,
  nameTransform = (s) => s,
  extraFields = [],
  showSynonyms = false,
}) {
  const [tab, setTab] = useState('library');
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Edit / create dialog
  const [editing, setEditing] = useState(null); // null = closed; {} = create; {id,...} = update
  const [saving, setSaving] = useState(false);

  // Approve dialog (pending tab)
  const [approveItem, setApproveItem] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [libRes, pendRes] = await Promise.all([
        api.list({ pending: false }),
        api.list({ pending: true }),
      ]);
      setItems(libRes.data?.results || []);
      setPending(pendRes.data?.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filterCategory && it.category !== filterCategory) return false;
      if (q) {
        const needle = q.toLowerCase();
        const hay = [
          it.name, it.description, ...(it.synonyms || []),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [items, q, filterCategory]);

  const beginCreate = () => setEditing({});
  const beginEdit = (it) => setEditing({ ...it });

  const handleSave = async () => {
    if (!editing) return;
    const payload = {
      name: nameTransform((editing.name || '').trim()),
      category: editing.category,
      description: (editing.description || '').trim() || null,
    };
    if (showSynonyms) {
      payload.synonyms = (editing.synonymsRaw ?? (editing.synonyms || []).join(', '))
        .split(',').map((s) => s.trim()).filter(Boolean);
    }
    extraFields.forEach((f) => {
      const v = editing[f.key];
      if (v === undefined) {
        payload[f.key] = null;
      } else if (typeof v === 'boolean' || Array.isArray(v)) {
        payload[f.key] = v;
      } else {
        payload[f.key] = (v || '').toString().trim() || null;
      }
    });
    if (!payload.name) { toast.error('Name is required'); return; }
    if (!payload.category) { toast.error('Category is required'); return; }

    setSaving(true);
    try {
      if (editing.id) {
        await api.update(editing.id, payload);
        toast.success(`${labels.singular} updated`);
      } else {
        await api.create(payload);
        toast.success(`${labels.singular} created`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDialog = useConfirm();
  const handleDelete = async (it) => {
    if (!(await confirmDialog({ title: `Delete this ${labels.singular}?`, detail: it.name, confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await api.delete(it.id);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleApprove = async () => {
    if (!approveItem) return;
    try {
      const payload = mergeTargetId
        ? { merge_into_id: mergeTargetId }
        : {
            name: nameTransform((approveItem.name || '').trim()) || undefined,
            category: approveItem.category || undefined,
            description: (approveItem.description || '').trim() || undefined,
          };
      await api.approve(approveItem.id, payload);
      toast.success(mergeTargetId ? 'Merged' : 'Approved');
      setApproveItem(null);
      setMergeTargetId('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    }
  };

  const handleReject = async (it) => {
    if (!(await confirmDialog({ title: `Reject this pending ${labels.singular}?`, detail: it.name, confirmLabel: 'Reject', destructive: true }))) return;
    try {
      await api.reject(it.id);
      toast.success('Rejected');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    }
  };

  return (
    <div className="space-y-6" data-testid={`${testIdPrefix}-page`}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-foreground">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        <Button
          onClick={beginCreate}
          data-testid={`${testIdPrefix}-new-btn`}
        >
          <Plus className="w-4 h-4 mr-1.5" />
          New {labels.singular}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="library" data-testid={`${testIdPrefix}-tab-library`}>
            Library ({items.length})
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid={`${testIdPrefix}-tab-pending`}>
            Pending ({pending.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="space-y-4">
          <Card>
            <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={labels.search}
                  className="pl-9"
                  data-testid={`${testIdPrefix}-search`}
                />
              </div>
              <div className="w-full sm:w-56">
                <Select value={filterCategory || 'all'} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
                  <SelectTrigger data-testid={`${testIdPrefix}-category-filter`}>
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{labels.name}</TableHead>
                    <TableHead>Category</TableHead>
                    {showSynonyms && <TableHead>Synonyms</TableHead>}
                    <TableHead className="hidden md:table-cell">Description</TableHead>
                    <TableHead className="text-right">Usage</TableHead>
                    <TableHead className="text-right w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</TableCell></TableRow>
                  )}
                  {!loading && filtered.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      No {labels.plural} match.
                    </TableCell></TableRow>
                  )}
                  {filtered.map((it) => (
                    <TableRow key={it.id} data-testid={`${testIdPrefix}-row-${it.id}`}>
                      <TableCell className="font-medium">
                        {it.display_name && it.display_name !== it.name ? (
                          <>
                            <div>{it.display_name}</div>
                            <div className="text-xs text-muted-foreground font-normal font-mono">{it.name}</div>
                          </>
                        ) : (
                          it.name
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">{it.category}</Badge>
                      </TableCell>
                      {showSynonyms && (
                        <TableCell className="text-xs text-muted-foreground">
                          {(it.synonyms || []).slice(0, 3).join(', ')}
                          {(it.synonyms || []).length > 3 ? '…' : ''}
                        </TableCell>
                      )}
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground max-w-md truncate">
                        {it.description || '—'}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {it.usage_count || 0}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => beginEdit(it)}
                            data-testid={`${testIdPrefix}-edit-${it.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(it)}
                            data-testid={`${testIdPrefix}-delete-${it.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="space-y-4">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && pending.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                Nothing is waiting for review. New {labels.plural} appear here once the AI proposes them.
              </CardContent>
            </Card>
          )}
          {pending.map((it) => (
            <Card key={it.id} className="bg-ground" data-testid={`${testIdPrefix}-pending-${it.id}`}>
              <CardContent className="p-4 sm:p-5 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold text-foreground">{it.name}</h3>
                      <Badge variant="secondary">{it.category}</Badge>
                      <Badge variant="secondary" className="text-xs">
                        proposed {it.proposal_count || 1}×
                      </Badge>
                    </div>
                    {it.description && (
                      <p className="text-sm text-muted-foreground mt-1">{it.description}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => { setApproveItem({ ...it }); setMergeTargetId(''); }}
                      data-testid={`${testIdPrefix}-approve-${it.id}`}
                    >
                      <Check className="w-4 h-4 mr-1.5" /> Approve
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="text-destructive"
                      onClick={() => handleReject(it)}
                      data-testid={`${testIdPrefix}-reject-${it.id}`}
                    >
                      <X className="w-4 h-4 mr-1.5" /> Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Create / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid={`${testIdPrefix}-edit-dialog`}>
          <DialogHeader>
            <DialogTitle>
              {editing?.id ? `Edit ${labels.singular}` : `New ${labels.singular}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editing?.name || ''}
                onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                data-testid={`${testIdPrefix}-edit-name`}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={editing?.category || ''}
                onValueChange={(v) => setEditing((p) => ({ ...p, category: v }))}
              >
                <SelectTrigger data-testid={`${testIdPrefix}-edit-category`}>
                  <SelectValue placeholder="Choose category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                rows={3}
                value={editing?.description || ''}
                onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))}
                data-testid={`${testIdPrefix}-edit-description`}
              />
            </div>
            {showSynonyms && (
              <div className="space-y-2">
                <Label>Synonyms (comma-separated)</Label>
                <Input
                  value={editing?.synonymsRaw ?? (editing?.synonyms || []).join(', ')}
                  onChange={(e) => setEditing((p) => ({ ...p, synonymsRaw: e.target.value }))}
                  placeholder="e.g. lac review, looked-after review"
                  data-testid={`${testIdPrefix}-edit-synonyms`}
                />
              </div>
            )}
            {extraFields.map((f) => (
              <div key={f.key} className="space-y-2">
                {f.render ? (
                  f.render(editing, setEditing, testIdPrefix)
                ) : (
                  <>
                    <Label>{f.label}</Label>
                    {f.type === 'textarea' ? (
                      <Textarea
                        rows={2}
                        value={editing?.[f.key] || ''}
                        onChange={(e) => setEditing((p) => ({ ...p, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        data-testid={`${testIdPrefix}-edit-${f.key}`}
                      />
                    ) : (
                      <Input
                        value={editing?.[f.key] || ''}
                        onChange={(e) => setEditing((p) => ({ ...p, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        data-testid={`${testIdPrefix}-edit-${f.key}`}
                      />
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid={`${testIdPrefix}-edit-save`}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={!!approveItem} onOpenChange={(o) => !o && setApproveItem(null)}>
        <DialogContent data-testid={`${testIdPrefix}-approve-dialog`}>
          <DialogHeader>
            <DialogTitle>Approve {labels.singular}</DialogTitle>
            <DialogDescription>
              Approve as-is (with optional edits) or merge into an existing {labels.singular}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Merge into existing {labels.singular}</Label>
              <Select value={mergeTargetId || 'none'} onValueChange={(v) => setMergeTargetId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid={`${testIdPrefix}-merge-select`}>
                  <SelectValue placeholder={`No merge — approve as new ${labels.singular}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No merge — approve as new</SelectItem>
                  {items
                    .filter((it) => approveItem?.category ? it.category === approveItem.category : true)
                    .map((it) => (
                      <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {!mergeTargetId && approveItem && (
              <>
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={approveItem.name || ''}
                    onChange={(e) => setApproveItem((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={approveItem.category || ''}
                    onValueChange={(v) => setApproveItem((p) => ({ ...p, category: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    rows={3}
                    value={approveItem.description || ''}
                    onChange={(e) => setApproveItem((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setApproveItem(null)}>Cancel</Button>
            <Button onClick={handleApprove} data-testid={`${testIdPrefix}-approve-confirm`}>
              {mergeTargetId ? <><GitMerge className="w-4 h-4 mr-1.5" /> Merge</> : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
